import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { Zap, Flame, Info, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { useSite, tx } from "@/lib/lang";
import { ui } from "@/lib/uiStrings";
import { api } from "@/lib/api";
import { Reveal, SectionLabel } from "./Reveal";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";

export const fmtPrice = (v) =>
  v == null ? "—" : v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};
export const fmtChange = (x) =>
  x == null ? "—" : `${x > 0 ? "+" : ""}${x.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

export function ChangeChip({ value, testId }) {
  const neutral = value == null || Math.abs(value) < 0.1;
  const up = !neutral && value > 0;
  const Icon = neutral ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const cls = neutral
    ? "text-muted-foreground border-ice/15 bg-onyx/40"
    : up
      ? "text-orange-500 border-orange-500/30 bg-orange-500/10"
      : "text-mint border-mint/30 bg-mint/10";
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 text-sm font-semibold border rounded-full px-3 py-1 ${cls}`}>
      <Icon size={14} />
      {fmtChange(value)}
    </span>
  );
}

export function PriceTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-ice/15 rounded-lg px-3 py-2 shadow-lg">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{fmtDate(p.date)}</p>
      <p className="text-sm font-semibold text-ice">{fmtPrice(p.value)} €/MWh</p>
    </div>
  );
}

export function MiniChart({ data, testId }) {
  return (
    <div className="h-[110px]" data-testid={testId} role="img" aria-label="Grafico andamento giornaliero ultimi 30 giorni">
      <ResponsiveContainer width="100%" height="100%" debounce={80}>
        <LineChart data={data} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
          <XAxis dataKey="date" hide />
          <YAxis hide domain={["dataMin - 1", "dataMax + 1"]} />
          <RTooltip content={<PriceTooltip />} cursor={{ stroke: "rgba(58,91,217,0.25)" }} />
          <Line type="monotone" dataKey="value" stroke="#3A5BD9" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function PriceCard({ icon: Icon, title, sub, metric, series, linkTo, linkLabel, info, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-surface border border-ice/8 rounded-2xl p-7 sm:p-9 hover:border-royal/40 transition-colors duration-300 flex flex-col"
    >
      <div className="flex items-center gap-3.5">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-royal/10 border border-royal/20 text-royal shrink-0">
          <Icon size={20} />
        </span>
        <div>
          <h3 className="font-display text-lg font-semibold text-ice leading-tight flex items-center gap-1.5">
            {title}
            {info && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" data-testid={`${testId}-info`} className="text-muted-foreground hover:text-royal transition-colors" aria-label="info">
                      <Info size={14} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
                    {info}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </h3>
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground mt-0.5">{sub}</p>
        </div>
      </div>

      <div className="mt-7 flex items-baseline gap-2">
        <span data-testid={`${testId}-value`} className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-ice">
          {fmtPrice(metric?.value)}
        </span>
        <span className="text-sm text-muted-foreground">€/MWh</span>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Ultimo aggiornamento: {fmtDate(metric?.date)}
      </p>

      <div className="mt-6 flex items-end gap-10">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Media mese</p>
          <p className="mt-1.5 font-display text-lg font-bold text-ice">{fmtPrice(metric?.month_avg)} <span className="text-xs font-normal text-muted-foreground">€/MWh</span></p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Variazione giornaliera</p>
          <div className="mt-1.5"><ChangeChip value={metric?.change_pct} testId={`${testId}-change`} /></div>
        </div>
      </div>

      <div className="mt-7">
        <MiniChart data={series} testId={`${testId}-chart`} />
      </div>

      <Link
        data-testid={`${testId}-link`}
        to={linkTo}
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-royal hover:text-azure transition-colors"
      >
        {linkLabel}
        <ArrowUpRight size={15} />
      </Link>
    </div>
  );
}

export default function Osservatorio() {
  const { content, lang } = useSite();
  const t = ui[lang].osservatorio;
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/energy-prices/latest").then((r) => setData(r.data)).catch(() => {});
    api.get("/energy-prices/history", { params: { days: 30 } }).then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const punSeries = history.filter((d) => d.pun_eur_mwh != null).map((d) => ({ date: d.date, value: d.pun_eur_mwh }));
  const psvSeries = history.filter((d) => d.psv_eur_mwh != null).map((d) => ({ date: d.date, value: d.psv_eur_mwh }));

  return (
    <section id="osservatorio" data-testid="osservatorio-section" className="py-16 lg:py-24 bg-surface/30 border-y border-ice/5">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal className="max-w-2xl">
          <SectionLabel>{t.label}</SectionLabel>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-ice">
            {tx(content.sezioni?.osservatorio?.heading, lang) || t.heading}
          </h2>
          <p className="mt-4 text-base text-muted-foreground">
            {tx(content.sezioni?.osservatorio?.sub, lang) || t.sub}
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Reveal>
            <PriceCard
              icon={Zap}
              title={t.punTitle}
              sub={t.punSub}
              metric={data?.pun}
              series={punSeries}
              linkTo="/osservatorio-energia#pun"
              linkLabel={t.vediPun}
              testId="oss-card-pun"
            />
          </Reveal>
          <Reveal delay={0.08}>
            <PriceCard
              icon={Flame}
              title={t.psvTitle}
              sub={t.psvSub}
              metric={data?.psv}
              series={psvSeries}
              linkTo="/osservatorio-energia#psv"
              linkLabel={t.vediPsv}
              info={t.psvInfo}
              testId="oss-card-psv"
            />
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <p data-testid="osservatorio-fonte" className="mt-8 font-mono text-[11px] text-muted-foreground/70 leading-relaxed">
            {t.fonte}
            {data?.updated_at && (
              <>
                {" · "}{t.ultimoAgg}: {new Date(data.updated_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </>
            )}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
